// @ts-nocheck
/**
 * Role Hierarchy Visualization Component
 * Epic #17: RBAC & Permissions Platform Services
 *
 * Interactive tree diagram showing educational role hierarchy
 * Platform Admin (10) → District Admin (9) → Principal (7) → Teacher (5) → Parent (2) → Student (1)
 */

import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Modal, Tag, Tooltip, Loading, InlineNotification, Button, Tile } from '@carbon/react';
import { Reset, ZoomIn, ZoomOut } from '@carbon/icons-react';
import type { RoleHierarchyData, RoleHierarchyResponse } from '../../../../shared/interfaces/rbac.interface';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';
import styles from './RoleHierarchyVisualization.module.scss';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface RoleHierarchyVisualizationProps {
  height?: number;
  width?: number;
}

interface TreeNode {
  data: RoleHierarchyData;
  x?: number;
  y?: number;
  children?: TreeNode[];
  parent?: TreeNode | null;
}

const RoleHierarchyVisualization: React.FC<RoleHierarchyVisualizationProps> = ({ height = 600, width = 1200 }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { token } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hierarchyData, setHierarchyData] = useState<RoleHierarchyResponse | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleHierarchyData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Color mapping by hierarchy level using CSS custom properties
  const getColorByLevel = (level: number): string => {
    const colorMap: Record<number, string> = {
      10: 'var(--role-color-level-10)',
      9: 'var(--role-color-level-9)',
      7: 'var(--role-color-level-7)',
      5: 'var(--role-color-level-5)',
      3: 'var(--role-color-level-3)',
      2: 'var(--role-color-level-2)',
      1: 'var(--role-color-level-1)',
    };
    return colorMap[level] || 'var(--cds-text-secondary)';
  };

  // Fetch role hierarchy data
  const fetchHierarchyData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/v1/admin/rbac/role-hierarchy`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setHierarchyData(response.data.data);
        setError(null);
      } else {
        setError(response.data.message || 'Failed to load hierarchy data');
      }
    } catch (err: any) {
      console.error('Failed to fetch hierarchy data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load hierarchy data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHierarchyData();
  }, []);

  // Build tree structure from flat role data
  const buildTreeStructure = (roles: RoleHierarchyData[]): TreeNode | null => {
    if (!roles || roles.length === 0) return null;

    // Sort by hierarchy level descending (highest level first)
    const sortedRoles = [...roles].sort((a, b) => b.hierarchy_level - a.hierarchy_level);

    // Create nodes map
    const nodesMap = new Map<string, TreeNode>();
    sortedRoles.forEach(role => {
      nodesMap.set(role.role_id, { data: role, children: [] });
    });

    // Build parent-child relationships based on hierarchy levels
    let root: TreeNode | null = null;
    sortedRoles.forEach((role, index) => {
      const node = nodesMap.get(role.role_id);
      if (!node) return;

      if (index === 0) {
        // First node (highest level) is root
        root = node;
      } else {
        // Find parent (next higher level)
        const parent = sortedRoles.slice(0, index).find(r => r.hierarchy_level > role.hierarchy_level);

        if (parent) {
          const parentNode = nodesMap.get(parent.role_id);
          if (parentNode) {
            if (!parentNode.children) parentNode.children = [];
            parentNode.children.push(node);
            node.parent = parentNode;
          }
        }
      }
    });

    return root;
  };

  // Render D3 tree visualization
  useEffect(() => {
    if (!hierarchyData || !svgRef.current || !hierarchyData.roles.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove(); // Clear previous render

    const margin = { top: 20, right: 120, bottom: 20, left: 120 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Create zoom behavior
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 3])
      .on('zoom', event => {
        g.attr('transform', event.transform);
        setZoomLevel(event.transform.k);
      });

    svg.call(zoom);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Build tree structure
    const treeRoot = buildTreeStructure(hierarchyData.roles);
    if (!treeRoot) return;

    // Create tree layout
    const treeLayout = d3
      .tree<TreeNode>()
      .size([innerHeight, innerWidth])
      .separation((a, b) => (a.parent === b.parent ? 1 : 1.2));

    const root = d3.hierarchy<TreeNode>(treeRoot, d => d.children || []);
    const treeData = treeLayout(root as any);

    // Draw links (connections between nodes)
    g.selectAll('.link')
      .data(treeData.links())
      .enter()
      .append('path')
      .attr('class', 'role-hierarchy-link')
      .attr(
        'd',
        d3
          .linkHorizontal()
          .x((d: any) => d.y)
          .y((d: any) => d.x) as any
      );

    // Draw nodes
    const nodes = g
      .selectAll('.node')
      .data(treeData.descendants())
      .enter()
      .append('g')
      .attr('class', 'role-hierarchy-node')
      .attr('transform', (d: any) => `translate(${d.y},${d.x})`)
      .on('click', (event, d: any) => {
        event.stopPropagation();
        setSelectedRole(d.data.data);
        setModalOpen(true);
      });

    // Node circles
    nodes
      .append('circle')
      .attr('r', 28)
      .attr('fill', (d: any) => getColorByLevel(d.data.data.hierarchy_level));

    // Node hierarchy level text
    nodes
      .append('text')
      .attr('class', 'node-level-text')
      .attr('dy', '0.35em')
      .text((d: any) => d.data.data.hierarchy_level);

    // Role name labels
    nodes
      .append('text')
      .attr('class', 'node-name-text')
      .attr('dy', 45)
      .text((d: any) => d.data.data.role_name.replace('_', ' ').toUpperCase());

    // User count labels
    nodes
      .append('text')
      .attr('class', 'node-count-text')
      .attr('dy', 60)
      .text((d: any) => `${d.data.data.assigned_users} users`);
  }, [hierarchyData, width, height]);

  // Handle zoom controls
  const handleZoomIn = () => {
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition().call((d3.zoom() as any).scaleBy, 1.3);
    }
  };

  const handleZoomOut = () => {
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition().call((d3.zoom() as any).scaleBy, 0.7);
    }
  };

  const handleResetZoom = () => {
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition().call((d3.zoom() as any).transform, d3.zoomIdentity);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loading description="Loading role hierarchy..." withOverlay={false} />
      </div>
    );
  }

  if (error) {
    return (
      <InlineNotification
        kind="error"
        title="Error Loading Hierarchy"
        subtitle={error}
        onCloseButtonClick={() => setError(null)}
      />
    );
  }

  if (!hierarchyData || !hierarchyData.roles.length) {
    return (
      <InlineNotification kind="warning" title="No Data Available" subtitle="Role hierarchy data could not be loaded" />
    );
  }

  return (
    <div ref={containerRef} className={styles.container}>
      {/* Zoom Controls */}
      <div className={styles.zoomControls}>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={ZoomIn}
          iconDescription="Zoom In"
          hasIconOnly
          onClick={handleZoomIn}
        />
        <Button
          kind="ghost"
          size="sm"
          renderIcon={ZoomOut}
          iconDescription="Zoom Out"
          hasIconOnly
          onClick={handleZoomOut}
        />
        <Button
          kind="ghost"
          size="sm"
          renderIcon={Reset}
          iconDescription="Reset Zoom"
          hasIconOnly
          onClick={handleResetZoom}
        />
      </div>

      {/* Zoom Level Indicator */}
      <div className={styles.zoomIndicator}>
        <Tag type="blue" size="sm">
          Zoom: {(zoomLevel * 100).toFixed(0)}%
        </Tag>
      </div>

      {/* SVG Tree Visualization */}
      <svg ref={svgRef} width={width} height={height} className={styles.svgVisualization} />

      {/* Role Details Modal */}
      <Modal
        open={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        modalHeading={selectedRole ? selectedRole.role_name.replace('_', ' ').toUpperCase() : 'Role Details'}
        passiveModal
        size="md"
      >
        {selectedRole && (
          <div className={styles.modalContent}>
            <div className={styles.modalSection}>
              <h4 className={styles.sectionLabel}>Hierarchy Level</h4>
              <Tag type="blue" size="md" style={{ backgroundColor: getColorByLevel(selectedRole.hierarchy_level) }}>
                Level {selectedRole.hierarchy_level}
              </Tag>
            </div>

            <div className={styles.modalSection}>
              <h4 className={styles.sectionLabel}>Assigned Users</h4>
              <p className={styles.sectionValue}>{selectedRole.assigned_users}</p>
            </div>

            <div className={styles.modalSection}>
              <h4 className={styles.sectionLabel}>Scope Type</h4>
              <p className={styles.sectionText}>{selectedRole.scope_type.replace('_', ' ')}</p>
            </div>

            {selectedRole.description && (
              <div className={styles.modalSection}>
                <h4 className={styles.sectionLabel}>Description</h4>
                <p className={styles.sectionText}>{selectedRole.description}</p>
              </div>
            )}

            <div className={styles.modalSection}>
              <h4 className={styles.sectionLabel}>Permissions ({selectedRole.permissions.length})</h4>
              <div className={styles.tagContainer}>
                {selectedRole.permissions.slice(0, 10).map((perm, index) => (
                  <Tag key={index} type="gray" size="sm">
                    {perm}
                  </Tag>
                ))}
                {selectedRole.permissions.length > 10 && (
                  <Tag type="gray" size="sm">
                    +{selectedRole.permissions.length - 10} more
                  </Tag>
                )}
              </div>
            </div>

            <div className={styles.modalSection}>
              <h4 className={styles.sectionLabel}>Permission Inheritance</h4>
              <Tag type={selectedRole.inherit_lower_permissions ? 'green' : 'gray'} size="sm">
                {selectedRole.inherit_lower_permissions ? 'Inherits lower role permissions' : 'No inheritance'}
              </Tag>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RoleHierarchyVisualization;
